import Button from '@pinecast/common/Button';
import {formatCurrency} from '@pinecast/common/helpers/format';
import {Crown} from '@pinecast/common/icons';
import StatusMessage from '@pinecast/common/StatusMessage';
import Tag from '@pinecast/common/Tag';
import {SectionSubheaderH2} from '@pinecast/common/Text';
import type {DonationPage_podcast} from '@pinecast/gql/__generated_tj__/DonationPage';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import type * as React from 'react';

import type {Frequency} from './TipFrequency';
import {unlocksPrivateEpisodes} from './tipAmounts';

const TipJarSummary = ({
  amount,
  error,
  frequency,
  onContinue,
  pending,
  podcast,
  unchanged = false,
}: {
  amount: number | null;
  /** Why Continue failed. It is shown here, and announced as an alert. */
  error: React.ReactNode;
  frequency: Frequency;
  onContinue: () => void;
  pending: boolean;
  podcast: DonationPage_podcast;
  /** The amount is what the subscriber gives now: there is nothing to do. */
  unchanged?: boolean;
}) => {
  const css = useCSS();
  const designColors = useColors();

  const formatted =
    amount === null ? null : formatCurrency(amount, podcast.tipJarCurrency);

  return (
    <div
      className={css({
        alignItems: 'center',
        borderTop: `1px solid ${designColors.borderGrayLight}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        paddingTop: '20px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          flex: '1 1 280px',
          flexDirection: 'column',
          gap: '4px',
        })}
      >
        <SectionSubheaderH2>
          <FormattedMessage
            description="Heading above the summary of what the listener is about to give on the tip jar page"
            defaultMessage="Your contribution"
          />
        </SectionSubheaderH2>
        <span
          className={css({
            fontFamily: 'Monument Grotesk Bold',
            fontSize: '19px',
            lineHeight: '26px',
          })}
        >
          {formatted === null ? (
            <FormattedMessage
              description="Summary on the tip jar page before the listener has picked an amount"
              defaultMessage="Choose an amount to continue"
            />
          ) : unchanged ? (
            <FormattedMessage
              description="Summary on the tip jar page while a subscriber has their current amount chosen. {amount} is a formatted currency amount."
              defaultMessage="{amount} every month now. Choose a new amount to continue."
              values={{amount: formatted}}
            />
          ) : frequency === 'recurring' ? (
            <FormattedMessage
              description="Summary of a recurring tip jar contribution. {amount} is a formatted currency amount."
              defaultMessage="{amount} every month"
              values={{amount: formatted}}
            />
          ) : (
            <FormattedMessage
              description="Summary of a single-month tip jar contribution. {amount} is a formatted currency amount."
              defaultMessage="{amount} once"
              values={{amount: formatted}}
            />
          )}
        </span>
        {amount !== null && unlocksPrivateEpisodes(podcast, amount) && (
          // Tag sets `flex: 0 0 0%`, which collapses it to nothing when it is a
          // direct child of a column flex container. The plain div keeps it out
          // of the flex layout so it sizes to its content.
          <div className={css({paddingTop: '2px'})}>
            <Tag color="yellow" size="medium">
              <span
                className={css({
                  alignItems: 'center',
                  display: 'inline-flex',
                  gap: '6px',
                })}
              >
                <Crown size={12} />
                <FormattedMessage
                  description="Tag in the tip jar summary confirming that the chosen amount includes private episodes"
                  defaultMessage="Private episodes included"
                />
              </span>
            </Tag>
          </div>
        )}
        {/* Its live regions are always here, so that a failure is announced;
            with nothing to say they take no space. */}
        <StatusMessage message={error} type="negative" />
      </div>
      <Button
        $isPrimary
        disabled={amount === null || unchanged}
        onClick={onContinue}
        pending={pending}
        size="large"
      >
        <FormattedMessage
          description="Button to move to the next step"
          defaultMessage="Continue"
        />
      </Button>
    </div>
  );
};

export default TipJarSummary;
